// FR-13's manual grid tools (P3.12).
//
// **Here rather than in a settings panel, because P3.12's rule says so and the rule is right:**
// "surface them where a wrong grid is noticed, which is the waveform, not a settings panel". A grid
// is noticed to be wrong while a track is playing and the beats are visibly not on the beats, which
// is three feet from where the user is looking and nowhere near a settings dialog.
//
// **Nothing here decides anything**, the same inversion `PerformancePads` describes. A press reports
// a gesture; `grid-edit.ts` does the arithmetic and refuses what it must; the loader stores the
// result and hands back what to draw. This file's whole job is buttons and a readout.

import { type JSX } from 'react';

import {
  GRID_NUDGE_MILLIS,
  TAP_TRUSTWORTHY,
  bpmOf,
  tapStage,
} from '../../platform/types/grid-edit';
import type { TrackLoaderApi } from './useTrackLoader';

/**
 * What the grid readout says.
 *
 * **Names which grid is on screen**, because "128.0 BPM" means two different things depending on
 * whether a human put it there, and the whole of FR-13 is about the difference. A corrected grid
 * that looked identical to a detected one would leave the user unable to tell whether their fix took
 * — and unable to tell, next session, that it had been remembered.
 */
export function describeGrid(loader: TrackLoaderApi): string {
  const grid = loader.beatGrid;
  if (grid === null) {
    return 'no grid';
  }
  const bpm = bpmOf(grid.beatSeconds).toFixed(2);
  const anchor = `${(grid.anchorSeconds * 1000).toFixed(0)} ms`;
  return loader.gridOverride === null
    ? `${bpm} BPM · downbeat ${anchor} · detected`
    : `${bpm} BPM · downbeat ${anchor} · corrected by hand`;
}

/**
 * How much of a trustworthy tap run has been collected, and what has been done with it.
 *
 * ⚠️ **The count is shown against `TAP_TRUSTWORTHY`, not against `TAP_MINIMUM`, and that is a
 * measurement rather than a preference.** Four taps is enough to *name* a tempo and is thirty times
 * outside AC-2's drift budget — 740 ms per minute against 25. A user who taps four times and stops
 * has a grid that looks corrected and drifts a beat every ten seconds, which is worse than the
 * honest low confidence they started from. The bar is what tells them to keep going; the numbers are
 * in `grid-edit.ts` under `TAP_TRUSTWORTHY`.
 *
 * ⚠️ **The bar existed and the code ignored it.** This readout asked for thirty-two taps while
 * `tapGrid` applied the grid at four, which is a UI telling the truth about a rule nothing enforced.
 * `tapStage` is now the single source for both, so the sentence here and the write to the store
 * cannot drift apart again.
 */
export function describeTaps(tapCount: number, previewBpm: number | null): string {
  if (tapCount === 0) {
    return 'tap along with the beat';
  }
  switch (tapStage(tapCount)) {
    case 'too-few':
      return `${String(tapCount)} — keep tapping`;
    // ⚠️ **Says "not applied yet", because until this it was applied and said the opposite.** The
    // count on its own read as progress towards *accuracy*, so a user seeing "4 of 32" reasonably
    // assumed the grid had not moved — and it had. Naming the preview tempo is what makes the run
    // worth continuing; naming its status is what makes the sentence true.
    case 'preview':
      return previewBpm === null
        ? `${String(tapCount)} of ${String(TAP_TRUSTWORTHY)} — keep tapping`
        : `${String(tapCount)} of ${String(TAP_TRUSTWORTHY)} — ${previewBpm.toFixed(2)} BPM so far, not applied yet`;
    case 'applied':
      return `${String(tapCount)} taps — accurate, applied`;
  }
}

export interface GridToolsProps {
  readonly loader: TrackLoaderApi;
}

export function GridTools({ loader }: GridToolsProps): JSX.Element {
  const deck = loader.deckId.toUpperCase();
  // Every tool needs a grid to act on except the tap, which is how you get one. A track that is
  // still loading has neither.
  const hasGrid = loader.beatGrid !== null;
  const corrected = loader.gridOverride !== null;

  return (
    <div className="stack stack--tight">
      <span className="micro">Beatgrid — {describeGrid(loader)}</span>

      <div className="row row--tight">
        <button
          type="button"
          onClick={loader.tapGrid}
          aria-label={`Tap tempo on deck ${deck}`}
        >
          Tap
        </button>
        <span className="micro mono">
          {describeTaps(loader.tapCount, loader.tapPreviewBpm)}
        </span>
        {loader.tapCount > 0 && (
          <button
            type="button"
            onClick={loader.resetTaps}
            aria-label={`Discard the current tap run on deck ${deck}`}
          >
            Clear taps
          </button>
        )}
      </div>

      {/* Nudge. Positive is later, so "→" moves the beats right along the waveform — the direction
          the label promises. Coarse is 10 ms because that is AC-2's own phase tolerance. */}
      <span className="micro">Shift the grid</span>
      <div className="row row--tight">
        {(
          [
            [-GRID_NUDGE_MILLIS.coarse, '−10 ms'],
            [-GRID_NUDGE_MILLIS.fine, '−1 ms'],
            [GRID_NUDGE_MILLIS.fine, '+1 ms'],
            [GRID_NUDGE_MILLIS.coarse, '+10 ms'],
          ] as const
        ).map(([millis, label]) => (
          <button
            key={label}
            type="button"
            onClick={() => {
              loader.nudgeGridBy(millis);
            }}
            disabled={!hasGrid}
            aria-label={`Shift deck ${deck}'s beatgrid by ${label}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Halve and double — the octave error, which is the single most common way a detector is
          wrong and the reason `tempo.ts` spends most of its length on the half/double decision. */}
      <span className="micro">Halve or double</span>
      <div className="row row--tight">
        <button
          type="button"
          onClick={() => {
            loader.scaleGrid(0.5);
          }}
          disabled={!hasGrid}
          aria-label={`Halve deck ${deck}'s tempo`}
        >
          ÷2
        </button>
        <button
          type="button"
          onClick={() => {
            loader.scaleGrid(2);
          }}
          disabled={!hasGrid}
          aria-label={`Double deck ${deck}'s tempo`}
        >
          ×2
        </button>
        {/* Only offered once there is something to undo. A "reset" beside an untouched grid invites
            the question of what it would reset to, and the answer is "nothing". */}
        <button
          type="button"
          onClick={loader.clearGridOverride}
          disabled={!corrected}
          aria-label={`Discard the manual grid correction on deck ${deck}`}
        >
          Reset
        </button>
      </div>

      {/* `role="status"` so a screen reader hears a refusal it did not ask for — UX §7. The element
          is always present rather than conditionally rendered, because an inserted live region is
          not reliably announced. */}
      <span className="micro" role="status">
        {loader.gridNote ?? ''}
      </span>
    </div>
  );
}
